import { Building2, Cpu, Globe, Monitor, Shield } from "lucide-react";
import type { HelpArticle } from "../types";

export const adminArticles: HelpArticle[] = [
  // ─────────────────────────────────────────── Admin dashboard
  {
    id: "admin-dashboard",
    title: "Admin Dashboard",
    icon: Globe,
    group: "admin",
    summary: "Platform-operator overview: company counts and licensed capacity across all tenants.",
    routeMatch: ["/admin/dashboard"],
    minRole: "super_admin",
    keywords: ["admin", "platform", "operator", "overview", "capacity", "companies", "health"],
    overview:
      "The **Admin Dashboard** is the Riot IO platform-operator landing page. It rolls up the company list into a few headline numbers — total and active companies, and the total licensed device and user capacity across every tenant.\n\n" +
      "It's read-only. This section is only visible to **Platform admins**.",
    concepts: [
      { term: "Licensed capacity", definition: "Total Device/User Capacity are the **sums of each company's limits**, not counts of real devices or users. They tell you how much you've licensed, not how much is in use." },
      { term: "System Health tiles", definition: "The API Gateway / DynamoDB / IoT Core tiles are static labels in this view, not a live monitor — don't read them as real-time status." },
    ],
    tasks: [
      {
        id: "admin-overview",
        title: "Get a platform overview",
        minRole: "super_admin",
        steps: [
          { text: "Open **Admin Dashboard**." },
          { text: "Read total vs active companies and the licensed capacity totals." },
          { text: "For real device counts or a specific tenant, use **All Devices** or **Companies**." },
        ],
      },
    ],
    related: ["admin-companies", "admin-devices"],
  },

  // ─────────────────────────────────────────── All Devices
  {
    id: "admin-devices",
    title: "All Devices",
    icon: Monitor,
    group: "admin",
    summary: "Platform-wide device inventory: add, edit, delete, reassign and bulk-act on devices across every tenant.",
    routeMatch: ["/admin/devices"],
    minRole: "super_admin",
    keywords: ["all devices", "admin devices", "reassign", "move", "add device", "cross-tenant", "bulk", "tenant"],
    overview:
      "**All Devices** is the cross-tenant device inventory for platform admins. It's where you actually add devices to a company, edit or delete them, move them between groups or tenants, and start bulk rollout/file actions.\n\n" +
      "Every write is attributed to the device's company. Bulk actions require your selection to be within a single company.",
    concepts: [
      { term: "Add device", definition: "Register a new device to a company by IMEI. You pick the company, optional group, type and model. (This is the only place devices are created.)" },
      { term: "Reassign / Move", definition: "Change a device's group (Move to group / Clear group), or move it to a different tenant entirely." },
      { term: "Device type vs model", definition: "**Type** is a hardware family (pick first); **Model** is a specific SKU within that type (enabled once a type is chosen)." },
      { term: "Company view (masquerade)", definition: "To see a single company's own scoped device page, masquerade into it from **Companies** — you'll land on the scoped `/devices` view. All Devices itself is always platform-wide." },
    ],
    tasks: [
      {
        id: "add-device-admin",
        title: "Add a device to a company",
        minRole: "super_admin",
        steps: [
          { text: "Open **All Devices** and click **Add device**." },
          { text: "Choose the **Company**, optionally a **Group**, and enter the **IMEI** (required)." },
          { text: "Optionally set **Name**, **Contact**, **Type** then **Model**, and a description." },
          { text: "Click **Add device**." },
        ],
        result: "The device is registered to that company (offline until it first checks in).",
      },
      {
        id: "reassign-device",
        title: "Move devices between groups or tenants",
        minRole: "super_admin",
        steps: [
          { text: "Tick one or more devices, then open the **Move** menu." },
          { text: "Choose **Move to group**, **Clear group assignment**, or **Move to tenant** (platform-wide view)." },
          { text: "The change applies to every selected device." },
        ],
      },
      {
        id: "bulk-admin-actions",
        title: "Bulk-create a task or attach files",
        minRole: "super_admin",
        steps: [
          { text: "Select devices that all belong to **one** company." },
          { text: "Click **Create task (n)** to jump into the rollout wizard, or **Add file (n)** to go to firmware upload, pre-scoped to that company and selection." },
          { text: "Use **Shadow** (single device) to open its configuration." },
        ],
      },
    ],
    faqs: [
      { q: "My bulk action was blocked with a 'select devices from one company' message.", a: "Cross-tenant bulk actions aren't allowed. Narrow your selection to a single company, then create the task / add the file." },
      { q: "How do I see just one company's devices?", a: "From **Companies**, masquerade into it — you'll get that company's scoped device list. All Devices is always the full, cross-tenant view." },
    ],
    related: ["admin-companies", "admin-device-types", "devices", "tasks"],
  },

  // ─────────────────────────────────────────── Companies
  {
    id: "admin-companies",
    title: "Companies",
    icon: Building2,
    group: "admin",
    summary: "Onboard tenants, set their tier/limits, activate or suspend them, and enter their workspace.",
    routeMatch: ["/admin/companies"],
    minRole: "super_admin",
    keywords: ["companies", "tenant", "onboard", "create company", "masquerade", "tier", "limits", "suspend", "activate", "override"],
    overview:
      "**Companies** is the tenant directory and control point. Search all companies, create new ones, and open a company to set its subscription tier, activate or suspend it, override its resource limits, and see live usage.\n\n" +
      "You can also **masquerade** into a company to work inside its tenant pages with your platform-admin access intact.",
    concepts: [
      { term: "Masquerade / Company view", definition: "Enter a company's context so tenant pages (Dashboard, Devices, Groups…) show that company's data. A gold banner shows it's active; **Exit company view** to leave. Navigating to any Platform Admin page automatically exits company view." },
      { term: "Tier", definition: "Basic, Standard or Premium — sets the default limits. Change it on the company detail page." },
      { term: "Status", definition: "Active, Pending or Suspended. Suspend to block a tenant; activate to restore it." },
      { term: "Override limits", definition: "Per-resource caps (devices, users, groups, firmware, concurrent tasks) applied on top of tier defaults. Blank keeps the current limit; `-` or 'unlimited' means no cap." },
    ],
    tasks: [
      {
        id: "create-company",
        title: "Onboard a new company",
        minRole: "super_admin",
        steps: [
          { text: "Open **Companies** and click **Add company**." },
          { text: "Enter the **Company name** and **Contact email**, then **Create company**.", note: "Only name and email are captured here; tier, status and limits use defaults until you set them." },
          { text: "From the success message, choose **Open details** to tune the tier and limits, or **Masquerade now** to work inside it." },
        ],
      },
      {
        id: "set-tier-limits",
        title: "Set a company's tier and limits",
        minRole: "super_admin",
        steps: [
          { text: "Open the company (row → detail page)." },
          { text: "In **Subscription Tier**, click the tier to apply." },
          { text: "In **Tenant Status**, use **Activate** or **Suspend**." },
          { text: "In **Override Limits**, fill the resources you want to cap (blank = keep, `-` = unlimited), then **Apply overrides**." },
        ],
      },
      {
        id: "masquerade",
        title: "Work inside a company (masquerade)",
        minRole: "super_admin",
        steps: [
          { text: "On the Companies list, click **Masquerade** (or **Manage groups** / **Open company workspace** on the detail page)." },
          { text: "Tenant pages now show that company's data — the gold **Company view active** banner confirms it." },
          { text: "Click **Exit company view** when done (or just open any Platform Admin page, which exits automatically)." },
        ],
      },
    ],
    faqs: [
      { q: "I masqueraded but the admin pages don't seem scoped.", a: "That's by design — opening any Platform Admin page automatically exits company view. Masquerade is for the *tenant* pages (Dashboard, Devices, Groups). The scoped device list lives at /devices while masquerading." },
      { q: "A tenant can't add a device or user.", a: "They may be at their limit. Check their **Usage** on the detail page and raise the relevant **Override limit** if appropriate." },
    ],
    related: ["admin-devices", "admin-users", "usage"],
  },

  // ─────────────────────────────────────────── Device Types
  {
    id: "admin-device-types",
    title: "Device Types",
    icon: Cpu,
    group: "admin",
    summary: "Define device families, their configurable-field schemas, and the hardware models that map to them.",
    routeMatch: ["/admin/device-types"],
    minRole: "super_admin",
    keywords: ["device type", "model", "schema", "config field", "catalogue", "field", "path", "kind", "section"],
    overview:
      "**Device Types** is the platform hardware catalogue. A **type** is a device family plus the **configuration-field schema** that decides which settings its devices expose; a **model** is a real hardware SKU mapped to a type.\n\n" +
      "These drive device onboarding, configuration validation and firmware/resource compatibility for every tenant.",
    concepts: [
      { term: "Device type", definition: "A family (e.g. Two-Way Communication Device) with a code, status, and a list of config fields. Its schema version bumps as fields change." },
      { term: "Config field", definition: "One setting a device exposes — a **path** (e.g. `audio.modem_out_gain`), a **label**, a **kind** (URL, phone, text, number, boolean, list…), a **section** for grouping, help text, and whether it's required." },
      { term: "Device model", definition: "A specific hardware SKU (e.g. RI-2-Way-Comms) mapped to a type. Models feed the model dropdowns used across the app (e.g. resource bundles)." },
      { term: "Schema is the contract", definition: "The schema defines exactly which config keys the backend accepts. Devices validate their configuration against it." },
    ],
    tasks: [
      {
        id: "create-device-type",
        title: "Create a device type and its schema",
        minRole: "super_admin",
        steps: [
          { text: "Open **Device Types**. In the create form, enter the **Name** and **Code** (lower-cased automatically)." },
          { text: "Set the **Status** and add **Configuration fields** — each needs a **Path** and **Label**; set **Kind**, **Section**, help text, placeholder and **Required** as needed." },
          { text: "Use **+ Add field** for more, then **Create device type**." },
        ],
        result: "The type is available for assigning to devices and for building resource bundles.",
      },
      {
        id: "create-model",
        title: "Add a device model",
        minRole: "super_admin",
        steps: [
          { text: "In the **Create device model** form, pick the **Device type**." },
          { text: "Enter the **Model code**, **Model name** and **Status**, plus an optional description." },
          { text: "Click **Create model**." },
        ],
      },
    ],
    faqs: [
      { q: "Can I edit an existing type or its fields here?", a: "This page creates types and models. Advanced field attributes (numeric min/max/step, curated enum options, defaults) and edits to an existing type's schema are configured outside this form." },
      { q: "The resource-bundle Model dropdown is empty for a tenant.", a: "Add a device **model** here (non-deprecated) mapped to the right type — the dropdown reads from this catalogue." },
    ],
    troubleshooting: [
      { problem: "After renaming or removing a config field, devices report an 'unsupported field' on save.", cause: "Devices still hold the old config key, which the new schema no longer accepts.", fix: "Devices self-heal: the shadow-settings save drops any key not in the current schema, so the next save succeeds. Prefer adding fields over renaming; treat a rename as a migration." },
      { problem: "A numeric config value outside the shown range wasn't rejected.", cause: "The schema validates the JSON type only; numeric ranges are enforced by the device firmware.", fix: "Rely on firmware to clamp/reject out-of-range values; use the range hints as guidance." },
    ],
    related: ["admin-devices", "resources", "devices"],
  },

  // ─────────────────────────────────────────── All Users
  {
    id: "admin-users",
    title: "All Users",
    icon: Shield,
    group: "admin",
    summary: "Cross-tenant user administration: invite, set roles, enable/disable and remove users in any company.",
    routeMatch: ["/admin/users"],
    minRole: "super_admin",
    keywords: ["all users", "admin users", "cross-tenant", "invite", "role", "disable", "delete", "tenant"],
    overview:
      "**All Users** is cross-tenant user administration for platform admins. Invite users into any company, change roles, enable or disable accounts, edit profiles, and remove users — each action attributed to that user's own company.\n\n" +
      "Platform-admin (super admin) accounts are read-only here.",
    concepts: [
      { term: "Cross-tenant scope", definition: "Each row shows the user's company; actions apply within that company automatically." },
      { term: "Roles", definition: "Viewer, Operator, Tenant admin (and super admin, which is read-only in this list)." },
      { term: "Search and filters", definition: "Search by email or name, and filter by company, role and status. These run across every user on the platform, not just the page on screen, and the count is the total that match." },
    ],
    tasks: [
      {
        id: "invite-user-admin",
        title: "Invite a user into a company",
        minRole: "super_admin",
        steps: [
          { text: "Open **All Users** and click **Add user**." },
          { text: "Pick the **Company**, enter the **Email** (and optional name), and choose a **Role**." },
          { text: "Click **Send invite**." },
        ],
      },
      {
        id: "manage-user-admin",
        title: "Change a role, disable or remove a user",
        minRole: "super_admin",
        steps: [
          { text: "Use the row **Role** dropdown to change a role, or **Edit** to change name/email/role together." },
          { text: "**Disable** blocks sign-in; **Enable** restores a deactivated user." },
          { text: "**Delete** removes them (record retained 30 days)." },
        ],
      },
    ],
    faqs: [
      { q: "Why can't I act on a super-admin row?", a: "Platform-admin accounts are protected — no role change, edit, disable or delete from this list." },
    ],
    related: ["admin-companies", "users"],
  },
];
